import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  AlertTriangle,
  Loader2,
  Sparkles,
  TrendingUp,
  Users,
} from "lucide-react";
import { Link } from "react-router-dom";

import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";

/**
 * R3 Phase 6A.7.2 — 30-day AI spend rollup.
 *
 * Two surfaces in one component:
 *
 *   1. <AISpendChip />  — compact pill rendered in the workflow
 *      library header.  Click → opens the drawer.  Renders nothing
 *      until the rollup query lands so we don't flash a "$0.00"
 *      before the real value arrives.
 *
 *   2. <AISpendDrawer /> — slide-out sheet with per-day bar chart,
 *      per-workflow breakdown, and (admin/builder only) per-operator
 *      breakdown.
 *
 * Backend already RBAC-gates: presenter/viewer see only their own
 * spend (`scope:"self"`); admin/builder see tenant-wide (`scope:"tenant"`).
 */
export default function AISpendChip({ testid = "ai-spend-chip" }) {
  const [open, setOpen] = useState(false);
  const { data, isLoading } = useQuery({
    queryKey: ["ai-spend-rollup", 30],
    queryFn: async () =>
      (await api.get("/workflows/ai-spend-rollup?window_days=30")).data,
    staleTime: 60_000,
  });

  if (isLoading || !data) {
    return (
      <span
        className="inline-flex h-7 w-24 animate-pulse rounded-full bg-slate-100"
        data-testid={`${testid}-skeleton`}
      />
    );
  }

  const total = Number(data.total_usd || 0);
  const count = data.generation_count || 0;
  const scopeLabel = data.scope === "self" ? " (you)" : "";

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button
          type="button"
          className="group inline-flex items-center gap-1.5 rounded-full border border-indigo-200 bg-indigo-50 px-2.5 py-1 text-[11px] font-medium text-indigo-700 transition-colors hover:bg-indigo-100 hover:border-indigo-300"
          data-testid={testid}
          data-scope={data.scope}
        >
          <Sparkles size={11} className="text-indigo-500" />
          <span>
            AI spend (30d)
            {scopeLabel}:{" "}
            <span className="font-mono font-semibold" data-testid={`${testid}-total`}>
              ${total.toFixed(2)}
            </span>
          </span>
          <span className="ml-0.5 text-[10px] text-indigo-500">{count} gens</span>
        </button>
      </SheetTrigger>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md"
        data-testid={`${testid}-drawer`}
      >
        <AISpendDrawerContent data={data} testid={testid} />
      </SheetContent>
    </Sheet>
  );
}

function AISpendDrawerContent({ data, testid }) {
  const perDayMax = useMemo(
    () =>
      (data.per_day || []).reduce(
        (acc, d) => Math.max(acc, Number(d.cost_usd) || 0),
        0,
      ),
    [data.per_day],
  );

  return (
    <>
      <SheetHeader>
        <SheetTitle className="flex items-center gap-2">
          <TrendingUp size={16} className="text-indigo-600" />
          AI spend — last {data.window_days} days
        </SheetTitle>
        <SheetDescription>
          {data.scope === "tenant"
            ? "Tenant-wide total across all operators."
            : "Your own generations only."}
        </SheetDescription>
      </SheetHeader>

      <div className="mt-4 space-y-5">
        <Section title="Summary">
          <div className="grid grid-cols-2 gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm">
            <Stat
              label="Total"
              value={`$${Number(data.total_usd || 0).toFixed(4)}`}
              testid={`${testid}-drawer-total`}
            />
            <Stat
              label="Generations"
              value={`${data.generation_count || 0}`}
              testid={`${testid}-drawer-count`}
            />
          </div>
        </Section>

        <Section title="Per day">
          {data.per_day?.length ? (
            <div
              className="flex h-24 items-end gap-0.5"
              role="img"
              aria-label="Per-day AI spend bar chart"
              data-testid={`${testid}-drawer-perday`}
            >
              {data.per_day.map((d) => {
                const v = Number(d.cost_usd) || 0;
                const pct = perDayMax > 0 ? (v / perDayMax) * 100 : 0;
                const isZero = v === 0;
                return (
                  <div
                    key={d.date}
                    className="group relative flex-1"
                    title={`${d.date}: $${v.toFixed(4)}`}
                  >
                    <div
                      className={`mx-auto w-full rounded-sm transition-colors ${
                        isZero
                          ? "bg-slate-200"
                          : "bg-indigo-400 group-hover:bg-indigo-600"
                      }`}
                      style={{ height: `${Math.max(2, pct)}%` }}
                    />
                  </div>
                );
              })}
            </div>
          ) : (
            <Empty />
          )}
        </Section>

        <Section title="Per workflow">
          {data.per_workflow?.length ? (
            <ul
              className="divide-y divide-slate-100 overflow-hidden rounded-lg border border-slate-200"
              data-testid={`${testid}-drawer-perworkflow`}
            >
              {data.per_workflow.map((w) => (
                <li
                  key={w.workflow_id}
                  className="flex items-center justify-between gap-2 px-3 py-2 text-[12px]"
                >
                  <Link
                    to={`/workflows/${w.workflow_id}`}
                    className="min-w-0 flex-1 truncate font-medium text-slate-800 hover:text-indigo-700"
                    title={w.workflow_name || w.workflow_id}
                  >
                    {w.workflow_name || w.workflow_id.slice(0, 8)}
                  </Link>
                  <span className="font-mono text-[11px] text-slate-500">
                    {w.generations}x
                  </span>
                  <span className="w-20 text-right font-mono font-semibold text-slate-900">
                    ${Number(w.cost_usd || 0).toFixed(4)}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <Empty />
          )}
        </Section>

        {Array.isArray(data.per_operator) && (
          <Section title="Per operator" icon={<Users size={12} className="opacity-60" />}>
            {data.per_operator.length ? (
              <ul
                className="divide-y divide-slate-100 overflow-hidden rounded-lg border border-slate-200"
                data-testid={`${testid}-drawer-peroperator`}
              >
                {data.per_operator.map((o) => (
                  <li
                    key={o.actor_email}
                    className="flex items-center justify-between gap-2 px-3 py-2 text-[12px]"
                  >
                    <span className="min-w-0 flex-1 truncate text-slate-800">
                      {o.actor_email}
                    </span>
                    <span className="font-mono text-[11px] text-slate-500">
                      {o.generations}x
                    </span>
                    <span className="w-20 text-right font-mono font-semibold text-slate-900">
                      ${Number(o.cost_usd || 0).toFixed(4)}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <Empty />
            )}
          </Section>
        )}
      </div>
    </>
  );
}

function Section({ title, icon, children }) {
  return (
    <div>
      <h4 className="mb-1.5 flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
        {icon}
        {title}
      </h4>
      {children}
    </div>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-400">
        {label}
      </div>
      <div className="mt-0.5 font-mono text-base font-semibold text-slate-900" data-testid={testid}>
        {value}
      </div>
    </div>
  );
}

function Empty() {
  return (
    <p className="rounded-md bg-slate-50 px-3 py-2 text-[11px] text-slate-400">
      No generations in this window yet.
    </p>
  );
}
